/**
 * Real HTTP-backed admin client.
 *
 * Mirrors the signatures of `admin-mock-client.ts` so the contract barrel
 * (admin-contract.ts) can swap the two with a single import switch.
 *
 * Scope: READ ONLY, EXCEPT the explicitly-marked staging-only demo-control
 * mutations at the bottom (L71/L72/L73I — simulate-failed-renewal and
 * synthetic-demo cleanup). Those are the first wired mutations and ride the
 * `@dangerous_action` confirmation flow (DangerousActionDialog) in the UI.
 * All other backend mutation endpoints remain unwired.
 *
 * Pagination:
 *   The backend now returns the same offset shape the frontend types use:
 *     { items, total, page, pageSize }
 *   We pass `page` / `pageSize` through unchanged and trust the response.
 *   A defensive fallback remains for malformed payloads (or legacy
 *   `{ items, nextCursor }` from a stale deploy) — see `toAdminPage`.
 *
 * Auth model:
 *   `adminHttp` already attaches `withCredentials: true`. If a bearer
 *   token is stored in the admin session helper, we forward it via
 *   `Authorization: Bearer <token>`. We never read tokens from
 *   localStorage / sessionStorage.
 */

import type { AdminGiftRiskQueue } from "@/types/admin-gift-support";
import type { AxiosRequestConfig } from "axios";

import { adminHttp } from "@/lib/api/admin-http-client";
import {
  ADMIN_READ_ENDPOINTS,
  ADMIN_MUTATION_ENDPOINTS,
} from "@/lib/api/admin-endpoints";
import type {
  AdminBillingRetry,
  SimulateFailedRenewalRequest,
  SimulateFailedRenewalResponse,
  CleanupSyntheticUserRequest,
  CleanupSyntheticUserResponse,
} from "@/types/admin-demo-controls";
import type {
  AdminSerialLoadRequest,
  AdminSerialLoadResponse,
} from "@/types/admin-fulfillment";
import type {
  AdminShipmentCarrierClaim,
  AdminShipmentListItem,
  AdminShipmentListParams,
} from "@/types/admin-shipment";
import type {
  AdminMembershipNoticeSummary,
  AdminMembershipNoticeRow,
  AdminMembershipNoticeListParams,
} from "@/types/admin-notice";
import type {
  AdminOutboxHealth,
  AdminEasypostWebhookHealth,
  AdminWebhookEventRow,
  AdminWebhookEventListParams,
} from "@/types/admin-webhook-health";
import { normalizeNoticeSummary } from "@/lib/api/admin-notice-summary-normalize";
import { normalizePaymentHistory } from "@/lib/api/admin-payments-normalize";
import type { AdminPaymentHistory } from "@/types/admin-payments";
import type {
  AdminDataExportRequest,
  AdminResearchExport,
  AdminResearchWithdrawal,
  AdminUserListItem,
  AdminUserProfile,
  AdminUserSearchParams,
  AdminUserSearchResult,
  AdminUserDetail,
  AdminSessionDetail,
  AdminTimelineEntry,
  AuthProvider,
} from "@/types/admin-user";
import type {
  AdminMembershipDetail,
  AdminMembershipListRow,
} from "@/types/admin-membership";
import type { AdminGiftListResponse } from "@/types/admin-gift";
import type { AdminGiftObligationsReport } from "@/types/admin-gift-obligations";
import { normalizeAdminMembershipDetail } from "@/lib/api/normalize-admin-membership";
import { normalizeAdminTimeline } from "@/lib/api/normalize-admin-timeline";
import type {
  AdminHardwareDetail,
  AdminHardwareListItem,
  AdminHardwareListParams,
  AdminHardwarePair,
} from "@/types/admin-hardware";
import type {
  AdminReclaimDetail,
  AdminReclaimListParams,
  AdminRestockingFeeStatus,
} from "@/types/admin-reclaim";
import type {
  AdminHardwareLostReport,
  AdminHardwareLostReportList,
  AdminHardwareReplacementOrder,
} from "@/types/admin-hardware-lost-reports";
import type { AdminReplacementPrice } from "@/types/admin-hardware-replacement-price";
import type { AdminAppVersionFloor, AdminAppVersionFloors } from "@/types/admin-app-version";
import type { AdminPolicyVersions } from "@/types/admin-policy-version";
import type { AdminRiskSignals, AdminFraudSignal } from "@/types/admin-fraud";
import type {
  AdminDisputeListItem,
  AdminDisputeDetail,
  AdminDisputeEvidence,
  AdminDisputeSearchParams,
} from "@/types/admin-dispute";
import type {
  AdminAuditEvent,
  AdminAuditSearchParams,
} from "@/types/admin-audit";
import type {
  AdminPage,
  AdminRiskLevel,
  AdminAccountStatus,
  AdminConsentState,
} from "@/types/admin-api";
import type {
  AdminOperationsSummary,
  AdminNavigationMetricsResponse,
  AdminMembershipFunnel,
} from "@/types/admin-operations";
import {
  SUPPORT_ACTION_QUEUE_STATES,
  type AdminSupportActionPlaceholder,
  type AdminSupportActionQueueRow,
  type AdminSupportActionSeverity,
  type SupportActionQueueState,
} from "@/types/admin-support";

/**
 * Optional bearer token getter. The auth provider can register itself
 * via `setAdminAuthTokenGetter(...)` at bootstrap. We keep this loosely
 * coupled so the real client doesn't pull a React context dependency.
 */
let _tokenGetter: (() => string | null | undefined) | null = null;

export function setAdminAuthTokenGetter(
  getter: (() => string | null | undefined) | null,
): void {
  _tokenGetter = getter;
}

export function authHeaders(): Record<string, string> {
  const token = _tokenGetter?.();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Read the backend response into the frontend `AdminPage<T>` offset shape.
 *
 * Happy path: the backend now returns `{ items, total, page, pageSize }`
 * directly, so we just pass it through.
 *
 * TODO(legacy-cursor-fallback): the `nextCursor` branch below is kept
 * for safety against a stale deploy that still returns the older cursor
 * shape. Remove once every environment is confirmed to be on the offset
 * release; the only loss is that `total` is synthesised when it lands.
 */
function toAdminPage<T>(
  data: unknown,
  params: { page?: number; pageSize?: number } = {},
): AdminPage<T> {
  if (
    typeof data === "object" &&
    data !== null &&
    "items" in data &&
    "total" in data &&
    "page" in data &&
    "pageSize" in data
  ) {
    return data as AdminPage<T>;
  }
  if (
    typeof data === "object" &&
    data !== null &&
    Array.isArray((data as { items?: unknown }).items) &&
    "nextCursor" in (data as Record<string, unknown>)
  ) {
    // Legacy cursor shape — keep working for a release window.
    const items = (data as { items: T[] }).items;
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? items.length;
    return { items, total: items.length, page, pageSize };
  }
  // Defensive: never throw on an unexpected payload — return empty.
  return {
    items: [],
    total: 0,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 0,
  };
}

/**
 * Pluck non-empty params into a flat record suitable for axios `params`.
 * undefined / null / "" are skipped so we don't emit `?foo=` keys.
 */
function pickQuery<T extends Record<string, unknown>>(
  params: T,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value === "") continue;
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      out[key] = value;
    }
  }
  return out;
}

async function get<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const resp = await adminHttp.get<T>(url, {
    ...config,
    headers: { ...(config?.headers ?? {}), ...authHeaders() },
  });
  return resp.data;
}

/**
 * POST with the same per-request bearer attach as `get`. Mutations MUST go
 * through here so the `Authorization: Bearer <token>` header is present on
 * the first attempt (not only after a 401-refresh) — otherwise a guarded
 * staging route answers 401 before the operator ever sees a result.
 */
async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const resp = await adminHttp.post<T>(url, body, {
    ...config,
    headers: { ...(config?.headers ?? {}), ...authHeaders() },
  });
  return resp.data;
}

/* ─── Operations ─────────────────────────────────────────────────── */

export async function getAdminOperationsSummary(): Promise<AdminOperationsSummary> {
  return get<AdminOperationsSummary>(ADMIN_READ_ENDPOINTS.operationsSummary);
}

export async function getAdminNavigationMetrics(): Promise<AdminNavigationMetricsResponse> {
  return get<AdminNavigationMetricsResponse>(
    ADMIN_READ_ENDPOINTS.navigationMetrics,
  );
}

/**
 * Real trial → paid funnel for the Memberships page pipeline card. Coerces
 * every count to a finite number at the seam so a missing/odd field can never
 * make the card divide-by or render `undefined.toLocaleString()`.
 */
export async function getAdminMembershipFunnel(): Promise<AdminMembershipFunnel> {
  const data = await get<Record<string, unknown>>(
    ADMIN_READ_ENDPOINTS.membershipFunnel,
  );
  const num = (v: unknown): number =>
    typeof v === "number" && Number.isFinite(v) ? v : 0;
  return {
    signups: num(data.signups),
    delivered: num(data.delivered),
    day21: num(data.day21),
    day31Attempted: num(data.day31Attempted),
    converted: num(data.converted),
    cardFailed: num(data.cardFailed),
    returned: num(data.returned),
    conversionRate: num(data.conversionRate),
    generatedAt: typeof data.generatedAt === "string" ? data.generatedAt : "",
    source: typeof data.source === "string" ? data.source : "real",
  };
}

/* ─── Users ──────────────────────────────────────────────────────── */

/**
 * Map a backend auth-provider token onto the dashboard `AuthProvider` enum.
 * The backend emits `"local"` for email+password accounts (the dashboard calls
 * that `"password"`); unknown values are dropped (null). Without this, the
 * Users list (`ProviderDot`) and the detail Overview tab (`AuthProviderChip`)
 * crash reading `.title`/`.icon` of an undefined config entry.
 */
function normalizeAuthProvider(value: unknown): AuthProvider | null {
  if (value === "google" || value === "apple") return value;
  if (value === "password" || value === "local" || value === "email") {
    return "password";
  }
  return null;
}

/**
 * Send the search term to the backend as `q`.
 *
 * `query` is not a parameter /admin/users/search binds, so sending it there was
 * the same as sending nothing: the endpoint returned an unfiltered page and the
 * dashboard pulled every page down to filter in the browser. The backend now
 * matches email, name, the opaque user id, a bound serial and the fulfillment
 * order's public id, which covers everything the client used to match plus the
 * two identifiers it never could.
 */
function toSearchQueryParams(
  params: AdminUserSearchParams,
): Record<string, unknown> {
  const { query, ...rest } = params;
  const term = typeof query === "string" ? query.trim() : "";
  return term ? { ...rest, q: term } : { ...rest };
}

export async function searchAdminUsers(
  params: AdminUserSearchParams = {},
): Promise<AdminUserSearchResult> {
  // Order-number lookup: a customer-facing fulfillment order id (fo_<random>)
  // can't be matched client-side (it isn't on the user row), so route it to the
  // backend as `q`, which resolves the owning user. Normal text queries stay on
  // the client-side-filtering path (they also cover userId/serial, which the
  // backend search does not) — so we deliberately do NOT send those as `q`.
  const outgoing = toSearchQueryParams(params);
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.searchUsers, {
    params: pickQuery(outgoing),
  });
  const page = toAdminPage<Record<string, unknown>>(data, params);
  // Backend user search currently omits several fields the dashboard type
  // expects (avatarUrl, path, futureBilling, primarySerial, authProviders,
  // lastActiveAt, riskFlags, countryCode). Normalise at the seam so list /
  // sort / filter / row-render code never sees null/undefined for required
  // string or array fields. Drop this once the backend ships them.
  const items = page.items.map((raw): AdminUserListItem => ({
    userId: String(
      (raw as { userId?: unknown; id?: unknown }).userId ??
        (raw as { id?: unknown }).id ??
        "",
    ),
    email: typeof raw.email === "string" ? raw.email : "",
    displayName:
      typeof raw.displayName === "string"
        ? raw.displayName
        : typeof raw.email === "string"
          ? raw.email
          : "",
    avatarUrl: typeof raw.avatarUrl === "string" ? raw.avatarUrl : null,
    // null = no membership row. The gateway sends that deliberately; coercing
    // it to a state (this used to default to "active") invents a lifecycle the
    // user does not have. Pass it through.
    membershipState:
      typeof raw.membershipState === "string"
        ? (raw.membershipState as AdminUserListItem["membershipState"])
        : null,
    path: readDetailPath(raw.path),
    futureBilling:
      raw.futureBilling === "annual" ||
      raw.futureBilling === "monthly" ||
      raw.futureBilling === "none"
        ? raw.futureBilling
        : "none",
    joinedAt:
      typeof raw.joinedAt === "string"
        ? raw.joinedAt
        : typeof raw.createdAt === "string"
          ? raw.createdAt
          : "",
    primarySerial:
      typeof raw.primarySerial === "string" ? raw.primarySerial : null,
    authProviders: Array.isArray(raw.authProviders)
      ? raw.authProviders
          .map(normalizeAuthProvider)
          .filter((p): p is AuthProvider => p !== null)
      : [],
    lastActiveAt:
      typeof raw.lastActiveAt === "string" ? raw.lastActiveAt : null,
    riskFlags: Array.isArray(raw.riskFlags)
      ? (raw.riskFlags as string[])
      : [],
    countryCode: typeof raw.countryCode === "string" ? raw.countryCode : "",
  }));
  return { ...page, items };
}

function deriveAccountStatus(
  raw: Record<string, unknown>,
): AdminAccountStatus {
  if (typeof raw.deletedAt === "string" && raw.deletedAt) return "deleted";
  if (typeof raw.lockedAt === "string" && raw.lockedAt) return "locked";
  return "active";
}

/**
 * Map the backend `AdminUserProfile` (id/fullName/photoUrl/country/authProvider/
 * lockedAt/deletedAt/lastSigninAt/createdAt) onto the dashboard's profile shape
 * (userId/displayName/avatarUrl/countryCode/authProviders[]/accountStatus/
 * joinedAt/lastActiveAt). The two share almost no field names.
 */
function normalizeUserProfile(
  raw: Record<string, unknown>,
  fallbackUserId: string,
  sessionsSummary: unknown,
): AdminUserProfile {
  // W65-7: the gateway sends no lifetime value and no device count on the
  // profile, and a filled-in 0 read as a fact. The device count it does send is
  // the active-session total in `sessionsSummary`.
  const activeSessions =
    sessionsSummary && typeof sessionsSummary === "object"
      ? (sessionsSummary as Record<string, unknown>).active
      : undefined;
  const displayName =
    (typeof raw.displayName === "string" && raw.displayName) ||
    (typeof raw.fullName === "string" && raw.fullName) ||
    (typeof raw.email === "string" && raw.email) ||
    "";
  const provider = normalizeAuthProvider(raw.authProvider);
  return {
    userId:
      raw.id === null || raw.id === undefined ? fallbackUserId : String(raw.id),
    email: typeof raw.email === "string" ? raw.email : "",
    displayName,
    avatarUrl: typeof raw.photoUrl === "string" ? raw.photoUrl : null,
    countryCode: typeof raw.country === "string" ? raw.country : "",
    locale: typeof raw.locale === "string" ? raw.locale : "",
    phoneE164: typeof raw.phone === "string" ? raw.phone : null,
    authProviders: provider ? [provider] : [],
    accountStatus: deriveAccountStatus(raw),
    joinedAt: typeof raw.createdAt === "string" ? raw.createdAt : "",
    lastActiveAt:
      typeof raw.lastSigninAt === "string" ? raw.lastSigninAt : null,
    ltvMinor: typeof raw.ltvMinor === "number" ? raw.ltvMinor : null,
    deviceCount:
      typeof raw.deviceCount === "number"
        ? raw.deviceCount
        : typeof activeSessions === "number"
          ? activeSessions
          : null,
  };
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * W65-8: the privacy requests the gateway sends on the user record. They used
 * to be dropped here, so the Consent tab read "Last withdrawal: never" while a
 * withdrawal was due. An absent block stays undefined — "unknown", not "never".
 */
function readResearchWithdrawal(raw: unknown): AdminResearchWithdrawal | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  return {
    status: readNonEmptyString(r.status),
    requestedAt: readNonEmptyString(r.requestedAt),
    dueAt: readNonEmptyString(r.dueAt),
    completedAt: readNonEmptyString(r.completedAt),
  };
}

function readResearchExport(raw: unknown): AdminResearchExport | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.eligible !== "boolean") return undefined;
  return {
    eligible: r.eligible,
    consentGrantedAt: readNonEmptyString(r.consentGrantedAt),
    eligibleSince: readNonEmptyString(r.eligibleSince),
    lastExportedAt: readNonEmptyString(r.lastExportedAt),
  };
}

function readDataExport(raw: unknown): AdminDataExportRequest | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  return {
    status: readNonEmptyString(r.status),
    requestedAt: readNonEmptyString(r.requestedAt),
    dueAt: readNonEmptyString(r.dueAt),
    completedAt: readNonEmptyString(r.completedAt),
  };
}

/** A nullable consent toggle. Absent/garbage → null ("never asked"), which the
 *  UI must not collapse into false. */
function readConsentFlag(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function readVersion(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/**
 * Backend consent block → dashboard `AdminConsentState`.
 *
 * The backend sends exactly the Auth Brief's consent contract (see
 * DashboardPrivacyResponse, which is `extra="forbid"`, so these key names are
 * the whole set): personalization, productImprovement, research,
 * researchRecontact, marketing, negativeOptionDisclosure, versionsAccepted.
 *
 * This used to read privacyPolicyVersion / termsVersion / termsAcceptedAt /
 * privacyPolicyAcceptedAt / deletionRequestedAt — none of which the backend has
 * ever emitted — so every one of them was permanently null while the real
 * toggles were dropped on the floor and the tab printed hardcoded values.
 */
function normalizeConsentState(raw: unknown): AdminConsentState {
  const c =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const versions =
    c.versionsAccepted && typeof c.versionsAccepted === "object"
      ? (c.versionsAccepted as Record<string, unknown>)
      : {};
  const acceptedAt =
    c.versionsAcceptedAt && typeof c.versionsAcceptedAt === "object"
      ? (c.versionsAcceptedAt as Record<string, unknown>)
      : {};
  return {
    personalization: true,
    productImprovement: readConsentFlag(c.productImprovement),
    research: readConsentFlag(c.research),
    researchRecontact: readConsentFlag(c.researchRecontact),
    marketing: readConsentFlag(c.marketing),
    negativeOptionDisclosure: readConsentFlag(c.negativeOptionDisclosure),
    versionsAccepted: {
      tos: readVersion(versions.tos),
      privacy: readVersion(versions.privacy),
      commitment: readVersion(versions.commitment),
      dataUse: readVersion(versions.dataUse),
      negativeOption: readVersion(versions.negativeOption),
    },
    // WF-72. Only string values survive — an acceptance date must be a real
    // timestamp from the backend or absent, never a coerced placeholder.
    versionsAcceptedAt: Object.fromEntries(
      Object.entries(acceptedAt).filter(
        ([, v]) => typeof v === "string" && v.length > 0,
      ),
    ) as AdminConsentState["versionsAcceptedAt"],
  };
}

/**
 * Backend purchase_path → the dashboard's path vocabulary.
 *
 * The gateway stores 'trial' | 'prepaid' | 'gift' (memberships CHECK
 * constraint). The dashboard says 'trial' | 'paid_trial' | 'direct' | 'comp',
 * with USER_PATH_LABEL rendering direct as "prepaid" and comp as "gift".
 *
 * Translating explicitly matters most for 'gift': it used to fall through to
 * the "direct" default and read as prepaid, but a gift is non-renewing by
 * default while prepaid auto-renews — so the mislabel told support the wrong
 * thing about a customer's money.
 */
const BACKEND_PATH_TO_DASHBOARD: Record<string, AdminUserDetail["path"]> = {
  trial: "trial",
  prepaid: "direct",
  gift: "comp",
  // The dashboard's own vocabulary, accepted as-is so mock mode and any
  // already-translated payload round-trip unchanged.
  paid_trial: "paid_trial",
  direct: "direct",
  comp: "comp",
};

function readDetailPath(value: unknown): AdminUserDetail["path"] {
  return typeof value === "string"
    ? (BACKEND_PATH_TO_DASHBOARD[value] ?? "direct")
    : "direct";
}

export async function getAdminUserDetail(
  userId: string,
): Promise<AdminUserDetail> {
  // Backend: { profile, membership, hardware, consent, reclaim, sessionsSummary }
  // with a profile shape that differs from the dashboard's. Normalize to the
  // flat AdminUserDetail so the header/summary/tabs never see undefined
  // (user.riskFlags.map / consent.* would otherwise crash the drawer).
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userDetail(userId));
  const obj =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const profileRaw =
    obj.profile && typeof obj.profile === "object"
      ? (obj.profile as Record<string, unknown>)
      : obj; // tolerate a flat/legacy profile-at-root payload
  const membership =
    obj.membership && typeof obj.membership === "object"
      ? (obj.membership as Record<string, unknown>)
      : {};
  const consentRaw =
    obj.consent && typeof obj.consent === "object"
      ? (obj.consent as Record<string, unknown>)
      : {};
  return {
    profile: normalizeUserProfile(profileRaw, userId, obj.sessionsSummary),
    consent: normalizeConsentState(obj.consent),
    researchWithdrawal: readResearchWithdrawal(
      obj.researchWithdrawal ?? consentRaw.researchWithdrawal,
    ),
    researchExport: readResearchExport(obj.researchExport),
    dataExport: readDataExport(obj.dataExport ?? consentRaw.dataExport),
    primarySerial:
      typeof obj.primarySerial === "string" ? obj.primarySerial : null,
    riskLevel: readRiskLevel(obj.riskLevel),
    riskFlags: Array.isArray(obj.riskFlags) ? (obj.riskFlags as string[]) : [],
    hasOpenDispute: obj.hasOpenDispute === true,
    hasOpenReclaim:
      obj.hasOpenReclaim === true ||
      Boolean(obj.reclaim && typeof obj.reclaim === "object"),
    path: readDetailPath(
      membership.purchasePath ?? membership.path ?? obj.path,
    ),
    // null = no membership row (the gateway's not_configured stub). Coercing it
    // to "active" showed support a green ACTIVE pill for accounts that never
    // completed checkout — see admin-real-client.no-membership.test.ts.
    membershipState:
      typeof membership.state === "string"
        ? (membership.state as AdminUserDetail["membershipState"])
        : typeof obj.membershipState === "string"
          ? (obj.membershipState as AdminUserDetail["membershipState"])
          : null,
  };
}

export async function getAdminUserTimeline(
  userId: string,
): Promise<AdminTimelineEntry[]> {
  // The backend row shape (source/type/severity/reason/actor:str) differs from
  // the dashboard's AdminTimelineEntry (entryId/kind/tone/actor:object), so
  // normalize before it reaches mapTimelineEntry — otherwise state_change rows
  // render with a missing key, fallback tone/icon, and a dropped reason.
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userTimeline(userId));
  return normalizeAdminTimeline(data);
}

export async function getAdminUserAuditEvents(
  userId: string,
): Promise<AdminAuditEvent[]> {
  return get<AdminAuditEvent[]>(ADMIN_READ_ENDPOINTS.userAuditEvents(userId));
}

/**
 * Map a backend session row → dashboard `AdminSessionDetail`. Backend row is
 * `{id,surface,deviceInfo,userAgent,expiresAt,revokedAt,lastSeenAt,createdAt}`;
 * the dashboard wants `{sessionId,deviceLabel,ipAddress,current,…}`. SessionCard
 * does `deviceLabel.toLowerCase()` + `maskIp(ipAddress).split(".")`, so those
 * MUST be strings — undefined crashed the Sessions tab.
 */
function normalizeSession(raw: Record<string, unknown>): AdminSessionDetail {
  const deviceLabel =
    (typeof raw.deviceLabel === "string" && raw.deviceLabel) ||
    (typeof raw.deviceInfo === "string" && raw.deviceInfo) ||
    (typeof raw.surface === "string" && raw.surface) ||
    (typeof raw.userAgent === "string" && raw.userAgent) ||
    "Unknown device";
  const createdAt = typeof raw.createdAt === "string" ? raw.createdAt : "";
  return {
    sessionId: String(raw.sessionId ?? raw.id ?? ""),
    createdAt,
    lastSeenAt: typeof raw.lastSeenAt === "string" ? raw.lastSeenAt : createdAt,
    ipAddress:
      typeof raw.ipAddress === "string"
        ? raw.ipAddress
        : typeof raw.lastSigninIp === "string"
          ? raw.lastSigninIp
          : "",
    userAgent: typeof raw.userAgent === "string" ? raw.userAgent : "",
    deviceLabel,
    current: raw.current === true,
    revokedAt: typeof raw.revokedAt === "string" ? raw.revokedAt : null,
  };
}

export async function getAdminUserSessions(
  userId: string,
): Promise<AdminSessionDetail[]> {
  // Backend returns `{ items: [...] }` with a per-row shape that differs from
  // the dashboard's; unwrap + normalize so SessionCard never touches undefined.
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userSessions(userId));
  const rawItems: unknown[] = Array.isArray(data)
    ? data
    : data &&
        typeof data === "object" &&
        Array.isArray((data as { items?: unknown }).items)
      ? (data as { items: unknown[] }).items
      : [];
  return rawItems.map((it) =>
    normalizeSession((it ?? {}) as Record<string, unknown>),
  );
}

export async function getAdminUserMembership(
  userId: string,
): Promise<AdminMembershipDetail> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userMembership(userId));
  if (typeof data === "object" && data !== null) {
    return normalizeAdminMembershipDetail(
      data as Record<string, unknown>,
      userId,
    );
  }
  return normalizeAdminMembershipDetail({}, userId);
}

/**
 * GET /admin/users/{userId}/gifts (read-only, `MEMBERSHIP_VIEW`). Trusts the
 * backend response shape as-is (no field-by-field normalization) — the wire
 * shape already matches `AdminGiftListResponse` (both hand-written to the
 * same contract; see `@/types/admin-gift`), and `status` is intentionally
 * `string`-shaped end to end (never a closed union) so an unrecognised value
 * degrades to a humanised label in `GiftsTab.tsx` instead of a thrown error.
 */
export async function getAdminUserGifts(
  userId: string,
): Promise<AdminGiftListResponse> {
  return get<AdminGiftListResponse>(ADMIN_READ_ENDPOINTS.userGifts(userId));
}

/**
 * GET /admin/reports/gift-obligations (read-only, `finance_reports:view`).
 *
 * Trusts the backend shape as-is, same as the gift list above and for the same
 * reason: both sides are hand-written to one contract, and `category` is
 * `string`-shaped end to end so an unrecognised bucket degrades to a readable
 * row instead of throwing away the whole report.
 *
 * OWNER-ONLY TODAY. Ryan asked for "Owner and authorized Ops Admins only", and
 * the gateway derives permissions from role alone — so granting the ops_admin
 * role would grant every ops admin. Until per-admin grants exist this 403s for
 * everyone but an owner, which the screen renders as a permission message rather
 * than an error.
 */
export async function getAdminGiftObligations(): Promise<AdminGiftObligationsReport> {
  return get<AdminGiftObligationsReport>(ADMIN_READ_ENDPOINTS.giftObligations);
}

/**
 * WS-F — gift redemptions flagged because several risk signals fired together.
 *
 * Read-only, and it never carries a gift code: the backend has no such field on
 * the response and must never grow one.
 */
export async function getAdminGiftRiskQueue(
  params: { state?: string } = {},
): Promise<AdminGiftRiskQueue> {
  const state = params.state ?? "open";
  return get<AdminGiftRiskQueue>(
    `${ADMIN_READ_ENDPOINTS.giftRiskReviews}?state=${encodeURIComponent(state)}`,
  );
}

/**
 * Map one backend hardware row → dashboard `AdminHardwareDetail`.
 *
 * Backend item: { id, userId, userEmail, serial, leftSerial, rightSerial,
 * status, boundAt, isRefurbished, lastSeenAt, createdAt, updatedAt }. The
 * dashboard type carries firmware/battery/shipping fields the backend does not
 * yet serve → default them (HardwareTab guards them with `?? "—"` / null
 * checks, so nulls render cleanly).
 */
function normalizeHardwareDevice(
  raw: Record<string, unknown>,
): AdminHardwareDetail {
  const status = raw.status;
  const serial =
    (typeof raw.serial === "string" && raw.serial) ||
    (typeof raw.leftSerial === "string" && raw.leftSerial) ||
    (typeof raw.rightSerial === "string" && raw.rightSerial) ||
    "";
  return {
    serial,
    status:
      typeof status === "string"
        ? (status as AdminHardwareDetail["status"])
        : "active",
    boundUserId:
      raw.userId === null || raw.userId === undefined
        ? null
        : String(raw.userId),
    boundAt: typeof raw.boundAt === "string" ? raw.boundAt : null,
    orderId: typeof raw.orderId === "string" ? raw.orderId : null,
    firmwareVersion:
      typeof raw.firmwareVersion === "string" ? raw.firmwareVersion : null,
    firmwareMinSupported:
      typeof raw.firmwareMinSupported === "string"
        ? raw.firmwareMinSupported
        : "",
    batteryPct: typeof raw.batteryPct === "number" ? raw.batteryPct : null,
    lastSeenAt: typeof raw.lastSeenAt === "string" ? raw.lastSeenAt : null,
    refurbished: raw.isRefurbished === true || raw.refurbished === true,
    shippingCarrier:
      typeof raw.shippingCarrier === "string" ? raw.shippingCarrier : null,
    shippingTrackingNumber:
      typeof raw.shippingTrackingNumber === "string"
        ? raw.shippingTrackingNumber
        : null,
    blacklisted: raw.blacklisted === true || status === "blacklisted",
    hardwareGeneration: raw.hardwareGeneration === "v2" ? "v2" : "v1",
  };
}

export async function getAdminUserHardware(
  userId: string,
): Promise<AdminHardwarePair> {
  // Backend serves { items: AdminHardwareDetail[] }; the dashboard wants a
  // { userId, primary, history } pair. Wrap + map at the seam.
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userHardware(userId));
  const rawItems: Record<string, unknown>[] =
    data && typeof data === "object" &&
    Array.isArray((data as { items?: unknown }).items)
      ? (data as { items: Record<string, unknown>[] }).items
      : Array.isArray(data)
        ? (data as Record<string, unknown>[])
        : [];
  const history = rawItems.map(normalizeHardwareDevice);
  // Prefer the device currently bound to this user as "primary"; else the first.
  const primary =
    history.find((d) => d.boundUserId === userId) ?? history[0] ?? null;
  return { userId, primary, history };
}

const RECLAIM_TRIGGER_SET = new Set<string>([
  "trial_return",
  "warranty",
  "rma",
  "fraud",
]);

const REFURB_QC_OUTCOME_SET = new Set<string>([
  "pass",
  "board_swap_required",
  "scrap",
  "damaged",
]);

/**
 * Fallback: map a raw backend RMA status → a dashboard pipeline stage, for the
 * (rare) path where the backend did not send a precomputed `stage`. Returns
 * null for non-pipeline statuses. The list endpoint normally sends `stage`
 * directly (see backend derive_reclaim_stage); this only covers older shapes.
 */
export function mapBackendStatusToStage(status: string): string | null {
  switch (status) {
    case "initiated":
      return "carrier_pending";
    case "label_issued":
      return "label_issued";
    case "in_transit":
      return "in_transit";
    case "received":
      return "warehouse_received";
    case "blocked":
      return "refurb_qc_pending";
    case "completed":
      return "refurb_passed"; // best-effort without side signals
    default:
      return null;
  }
}

/**
 * Map a backend reclaim object → dashboard `AdminReclaimDetail`. `flags` MUST
 * be an array (CarrierScanTimeline does `reclaim.flags.includes(...)`).
 */
function normalizeReclaimDetail(
  raw: Record<string, unknown>,
  userId: string,
): AdminReclaimDetail {
  const trigger = raw.trigger ?? raw.reasonCode;
  const rawStage = typeof raw.stage === "string" ? raw.stage : null;
  const rawStatus = typeof raw.status === "string" ? raw.status : null;
  const resolvedStatus =
    rawStage ?? (rawStatus ? mapBackendStatusToStage(rawStatus) : null) ?? "label_issued";
  return {
    id:
      typeof raw.id === "number"
        ? raw.id
        : Number.isFinite(Number(raw.id))
          ? Number(raw.id)
          : 0,
    rmaId: typeof raw.rmaId === "string" ? raw.rmaId : "",
    userId:
      raw.userId === null || raw.userId === undefined
        ? userId
        : String(raw.userId),
    serial: typeof raw.serial === "string" ? raw.serial : "",
    trigger:
      typeof trigger === "string" && RECLAIM_TRIGGER_SET.has(trigger)
        ? (trigger as AdminReclaimDetail["trigger"])
        : "rma",
    status: resolvedStatus as AdminReclaimDetail["status"],
    labelIssuedAt:
      typeof raw.labelIssuedAt === "string" ? raw.labelIssuedAt : null,
    lastScanAt: typeof raw.lastScanAt === "string" ? raw.lastScanAt : null,
    deadlineAt:
      typeof raw.deadlineAt === "string"
        ? raw.deadlineAt
        : typeof raw.expiresAt === "string"
          ? raw.expiresAt
          : null,
    carrier: typeof raw.carrier === "string" ? raw.carrier : null,
    trackingNumber:
      typeof raw.trackingNumber === "string" ? raw.trackingNumber : null,
    flags: Array.isArray(raw.flags) ? (raw.flags as string[]) : [],
    refurbNotes: typeof raw.refurbNotes === "string" ? raw.refurbNotes : null,
    // "failed" is load-bearing: a DECLINED $75 capture is money still owed. This
    // seam used to coerce it to null, so even after the gateway started sending it
    // the dashboard would have thrown it away.
    // Every fee state, not an allow-list. Narrowing this has hidden a fee twice
    // already — a declined capture, then a customer's contest.
    feeStatus: FEE_STATUS_SET.has(raw.feeStatus as string)
      ? (raw.feeStatus as AdminRestockingFeeStatus)
      : null,
    noticeSentAt: typeof raw.noticeSentAt === "string" ? raw.noticeSentAt : null,
    respondBy: typeof raw.respondBy === "string" ? raw.respondBy : null,
    contestedAt: typeof raw.contestedAt === "string" ? raw.contestedAt : null,
    contestNote: typeof raw.contestNote === "string" ? raw.contestNote : null,
    chargedOffAt:
      typeof raw.chargedOffAt === "string" ? raw.chargedOffAt : null,
    chargedOffAmountMinor:
      typeof raw.chargedOffAmountMinor === "number"
        ? raw.chargedOffAmountMinor
        : null,
    refurbQcOutcome: REFURB_QC_OUTCOME_SET.has(raw.refurbQcOutcome as string)
      ? (raw.refurbQcOutcome as AdminReclaimDetail["refurbQcOutcome"])
      : null,
    refurbished:
      typeof raw.refurbished === "boolean" ? raw.refurbished : null,
  };
}

export async function getAdminUserReclaim(
  userId: string,
): Promise<AdminReclaimDetail | null> {
  // Backend serves { active: <reclaim|null>, history: [...] }. The dashboard
  // wants the active reclaim (or null). Tolerate a bare object too.
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userReclaim(userId));
  if (!data || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  if ("active" in obj || "history" in obj) {
    const active = obj.active;
    if (active && typeof active === "object") {
      return normalizeReclaimDetail(active as Record<string, unknown>, userId);
    }
    // No active reclaim — surface the most recent history row carrying a
    // refurb-QC outcome. A completed refurb is terminal, so it lives only in
    // history; WF-65 still needs its QC outcome visible in the reclaim view.
    const history = Array.isArray(obj.history) ? obj.history : [];
    const withOutcome = history.find(
      (h) =>
        h != null &&
        typeof h === "object" &&
        (h as Record<string, unknown>).refurbQcOutcome != null,
    );
    return withOutcome
      ? normalizeReclaimDetail(withOutcome as Record<string, unknown>, userId)
      : null;
  }
  return normalizeReclaimDetail(obj, userId);
}

/**
 * Map one backend replacement-order object → dashboard
 * `AdminHardwareReplacementOrder`. Defensive at every field — the nested
 * order is optional on the backend row and its own fields may be absent.
 */
function normalizeHardwareReplacementOrder(
  raw: Record<string, unknown>,
): AdminHardwareReplacementOrder {
  return {
    publicId: typeof raw.publicId === "string" ? raw.publicId : "",
    status: typeof raw.status === "string" ? raw.status : "unknown",
    amountCents: typeof raw.amountCents === "number" ? raw.amountCents : null,
    currency: typeof raw.currency === "string" ? raw.currency : null,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : null,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : null,
  };
}

/**
 * Map one backend lost-report row → dashboard `AdminHardwareLostReport`.
 * `serials` MUST be an array and `replacementOrder` MUST be null (not
 * undefined) when absent, so HardwareTab can render both without guards.
 */
function normalizeHardwareLostReport(
  raw: Record<string, unknown>,
): AdminHardwareLostReport {
  const ro = raw.replacementOrder as Record<string, unknown> | null | undefined;
  return {
    id: typeof raw.id === "number" ? raw.id : 0,
    status: typeof raw.status === "string" ? raw.status : "unknown",
    serials: Array.isArray(raw.serials)
      ? raw.serials.filter((s): s is string => typeof s === "string")
      : [],
    stateAtReport:
      typeof raw.stateAtReport === "string" ? raw.stateAtReport : null,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : null,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : null,
    replacementOrder:
      ro && typeof ro === "object"
        ? normalizeHardwareReplacementOrder(ro)
        : null,
  };
}

/**
 * GET /admin/users/{userId}/hardware-lost-reports — backend serves
 * `{ items: [...] }`; normalize each row (+ its nested replacement order, if
 * any) at the seam so a missing field never reaches the read-only HardwareTab
 * render as undefined.
 */
export async function getAdminUserLostReports(
  userId: string,
): Promise<AdminHardwareLostReportList> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userLostReports(userId));
  const rawItems: unknown[] =
    data && typeof data === "object" &&
    Array.isArray((data as { items?: unknown }).items)
      ? (data as { items: unknown[] }).items
      : [];
  return {
    items: rawItems.map((it) =>
      normalizeHardwareLostReport((it ?? {}) as Record<string, unknown>),
    ),
  };
}

/**
 * GET /admin/hardware/replacement-price — the ONE global at-cost price.
 *
 * `amountCents: null` is the UNSET state, NOT an error: the backend answers 200
 * with nulls when no price has ever been set. Normalizing a missing/garbage
 * field to null (rather than 0) keeps "unset" distinguishable from "free" —
 * a 0 would render as "$0.00" and read as a real, set price.
 */
export async function getAdminReplacementPrice(): Promise<AdminReplacementPrice> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.hardwareReplacementPrice);
  const raw: Record<string, unknown> =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  return {
    amountCents: typeof raw.amountCents === "number" ? raw.amountCents : null,
    currency: typeof raw.currency === "string" ? raw.currency : null,
    setAt: typeof raw.setAt === "string" ? raw.setAt : null,
    setByAdminId:
      typeof raw.setByAdminId === "string" ? raw.setByAdminId : null,
  };
}

/**
 * `GET /admin/app-version` — WF-47 dimension 5.
 *
 * An all-null floor is the UNSET state, NOT an error: the backend answers 200
 * with nulls when no floor has been set for a platform. Normalizing
 * missing/garbage fields to null (never to "" or 0) keeps "no gate
 * configured" distinguishable from a real floor — mirrors
 * `getAdminReplacementPrice`'s null-safe normalizer above.
 */
function normalizeAppVersionFloor(raw: Record<string, unknown>): AdminAppVersionFloor {
  return {
    platform: typeof raw.platform === "string" ? raw.platform : "",
    minVersion: typeof raw.minVersion === "string" ? raw.minVersion : null,
    recommendedVersion:
      typeof raw.recommendedVersion === "string" ? raw.recommendedVersion : null,
    storeUrl: typeof raw.storeUrl === "string" ? raw.storeUrl : null,
    setAt: typeof raw.setAt === "string" ? raw.setAt : null,
    setByAdminId:
      typeof raw.setByAdminId === "string" ? raw.setByAdminId : null,
  };
}

function normalizeAppVersionFloors(data: unknown): AdminAppVersionFloors {
  const raw: Record<string, unknown> =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const list = Array.isArray(raw.floors) ? raw.floors : [];
  return {
    floors: list.map((entry) =>
      normalizeAppVersionFloor(
        entry && typeof entry === "object" ? (entry as Record<string, unknown>) : {},
      ),
    ),
  };
}

/**
 * WF-72 — the policy version in force per policy key.
 *
 * A key missing from the response, or carrying anything that is not a
 * non-empty string, is normalised to null: "not in force". Coercing an odd
 * value into a version would tell an owner a policy is live when it is not.
 */
export async function getAdminPolicyVersions(): Promise<AdminPolicyVersions> {
  const raw = await get<unknown>(ADMIN_READ_ENDPOINTS.adminPolicyVersions);
  const body = (raw ?? {}) as { policies?: unknown };
  const source =
    body.policies && typeof body.policies === "object"
      ? (body.policies as Record<string, unknown>)
      : {};
  const policies: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(source)) {
    policies[key] =
      typeof value === "string" && value.length > 0 ? value : null;
  }
  return { policies };
}

export async function getAdminAppVersionFloors(): Promise<AdminAppVersionFloors> {
  return normalizeAppVersionFloors(
    await get<unknown>(ADMIN_READ_ENDPOINTS.adminAppVersion),
  );
}

/* ─── Hardware (cross-user) ──────────────────────────────────────── */

export async function listAdminHardware(
  params: AdminHardwareListParams = {},
): Promise<AdminPage<AdminHardwareListItem>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.listHardware, {
    params: pickQuery(params as Record<string, unknown>),
  });
  return toAdminPage<AdminHardwareListItem>(data, params);
}

/* ─── Memberships (bulk list) ────────────────────────────────────── */

export interface AdminMembershipListParams {
  page?: number;
  pageSize?: number;
}

export async function listAdminMemberships(
  params: AdminMembershipListParams = {},
): Promise<AdminPage<AdminMembershipListRow>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.listMemberships, {
    params: pickQuery(params as Record<string, unknown>),
  });
  return toAdminPage<AdminMembershipListRow>(data, params);
}

/* ─── Reclaims (cross-user) ──────────────────────────────────────── */

export async function listAdminReclaims(
  params: AdminReclaimListParams = {},
): Promise<AdminPage<AdminReclaimDetail>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.listReclaims, {
    params: pickQuery(params as Record<string, unknown>),
  });
  const page = toAdminPage<Record<string, unknown>>(data, params);
  const items = page.items.map((row) =>
    normalizeReclaimDetail(row, String((row as { userId?: unknown }).userId ?? "")),
  );
  return { ...page, items };
}

/* ─── Fraud ──────────────────────────────────────────────────────── */

/** Every damage-fee state the gateway can report. Kept complete on purpose. */
const FEE_STATUS_SET = new Set<string>([
  "awaiting_response", "contested", "charged", "waived", "failed",
]);

const RISK_LEVEL_SET = new Set<string>(["low", "medium", "high", "critical"]);

function readRiskLevel(value: unknown): AdminRiskLevel {
  return typeof value === "string" && RISK_LEVEL_SET.has(value)
    ? (value as AdminRiskLevel)
    : "low";
}

/**
 * A signal's severity is on the backend's own info/warn/crit scale, NOT the
 * low/medium/high/critical one `riskLevel` uses. Reading it with readRiskLevel
 * silently floored every real signal to "low", so a blocked checkout rendered
 * at minimum severity. `warn` covers both route_to_prepaid and review, so it
 * maps to "medium"; the row's own riskLevel still reports "high" for a denial.
 */
const SIGNAL_LEVEL_TO_RISK: Record<string, AdminRiskLevel> = {
  info: "low",
  warn: "medium",
  crit: "critical",
};

function readSignalLevel(value: unknown): AdminRiskLevel {
  if (typeof value !== "string") return "low";
  return SIGNAL_LEVEL_TO_RISK[value] ?? readRiskLevel(value);
}

/**
 * Map a backend fraud signal { kind, level, detail, observedAt } → dashboard
 * `AdminFraudSignal` { kind, level, detectedAt, reason, evidence? }.
 */
function normalizeFraudSignal(raw: Record<string, unknown>): AdminFraudSignal {
  return {
    kind: (typeof raw.kind === "string"
      ? raw.kind
      : "") as AdminFraudSignal["kind"],
    level: readSignalLevel(raw.level),
    detectedAt:
      typeof raw.detectedAt === "string"
        ? raw.detectedAt
        : typeof raw.observedAt === "string"
          ? raw.observedAt
          : "",
    reason:
      typeof raw.reason === "string"
        ? raw.reason
        : typeof raw.detail === "string"
          ? raw.detail
          : "",
    evidence:
      raw.evidence && typeof raw.evidence === "object"
        ? (raw.evidence as Record<string, unknown>)
        : undefined,
  };
}

/**
 * Map the backend `AdminIdentityCluster` { collision, matchedAccountCount,
 * chargeOffCount } → the dashboard type, defaulting to a clean/empty cluster
 * when the field is absent (older backend) or malformed.
 */
function normalizeIdentityCluster(
  raw: unknown,
): AdminRiskSignals["identityCluster"] {
  const obj =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    collision: obj.collision === true,
    matchedAccountCount:
      typeof obj.matchedAccountCount === "number" ? obj.matchedAccountCount : 0,
    chargeOffCount:
      typeof obj.chargeOffCount === "number" ? obj.chargeOffCount : 0,
  };
}

/** Map one backend `AdminPriorChargeOff` { occurredAt, serials } → dashboard. */
function normalizePriorChargeOff(
  raw: Record<string, unknown>,
): AdminRiskSignals["priorChargeOffs"][number] {
  return {
    occurredAt: typeof raw.occurredAt === "string" ? raw.occurredAt : null,
    serials: Array.isArray(raw.serials)
      ? raw.serials.filter((s): s is string => typeof s === "string")
      : [],
  };
}

export async function getAdminUserRiskSignals(
  userId: string,
): Promise<AdminRiskSignals> {
  // Backend: { riskLevel, signals[], kindsCatalogue, identityCluster,
  // priorChargeOffs, source }. Dashboard wants { userId, riskLevel,
  // trialBlocked, householdExceptionGranted, signals, identityCluster,
  // priorChargeOffs, computedAt }. Fill the fields the backend omits.
  const data = await get<unknown>(
    ADMIN_READ_ENDPOINTS.userFraudSignals(userId),
  );
  const obj =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const signalsRaw = Array.isArray(obj.signals) ? obj.signals : [];
  const priorRaw = Array.isArray(obj.priorChargeOffs) ? obj.priorChargeOffs : [];
  return {
    userId:
      obj.userId === null || obj.userId === undefined
        ? userId
        : String(obj.userId),
    riskLevel: readRiskLevel(obj.riskLevel),
    trialBlocked: obj.trialBlocked === true,
    householdExceptionGranted: obj.householdExceptionGranted === true,
    householdMatchCount:
      typeof obj.householdMatchCount === "number" ? obj.householdMatchCount : 0,
    signals: signalsRaw.map((s) =>
      normalizeFraudSignal((s ?? {}) as Record<string, unknown>),
    ),
    identityCluster: normalizeIdentityCluster(obj.identityCluster),
    priorChargeOffs: priorRaw.map((p) =>
      normalizePriorChargeOff((p ?? {}) as Record<string, unknown>),
    ),
    computedAt: typeof obj.computedAt === "string" ? obj.computedAt : "",
  };
}

/* ─── Disputes ───────────────────────────────────────────────────── */

export async function listAdminDisputes(
  params: AdminDisputeSearchParams = {},
): Promise<AdminPage<AdminDisputeListItem>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.listDisputes, {
    params: pickQuery(params as Record<string, unknown>),
  });
  return toAdminPage<AdminDisputeListItem>(data, params);
}

export async function getAdminDisputeDetail(
  disputeId: string,
): Promise<AdminDisputeDetail> {
  return get<AdminDisputeDetail>(ADMIN_READ_ENDPOINTS.disputeDetail(disputeId));
}

export async function getAdminDisputeEvidence(
  disputeId: string,
): Promise<AdminDisputeEvidence> {
  return get<AdminDisputeEvidence>(
    ADMIN_READ_ENDPOINTS.disputeEvidence(disputeId),
  );
}

/* ─── Audit logs ─────────────────────────────────────────────────── */

export async function listAdminAuditLogs(
  params: AdminAuditSearchParams = {},
): Promise<AdminPage<AdminAuditEvent>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.auditLogs, {
    params: pickQuery(params as Record<string, unknown>),
  });
  return toAdminPage<AdminAuditEvent>(data, params);
}

export async function getAdminAuditEvent(
  eventId: string,
): Promise<AdminAuditEvent> {
  return withAuditDiff(
    await get<AuditEventDetailWire>(ADMIN_READ_ENDPOINTS.auditEvent(eventId)),
  );
}

/** The gateway's audit detail: the change comes as top-level `before` / `after`. */
type AuditEventDetailWire = AdminAuditEvent & { before?: unknown; after?: unknown };

/**
 * OBS-9: the event panel reads `diff`, which only the mock data had — so on staging every
 * event said "No diff recorded", and a filed claim's reference showed nowhere. Map the
 * gateway's `before` / `after` into it; an event that recorded no change keeps none.
 */
function withAuditDiff(wire: AuditEventDetailWire): AdminAuditEvent {
  const { before, after, ...event } = wire;
  if (event.diff !== undefined) return event;
  if (before == null && after == null) return event;
  return { ...event, diff: { before: before ?? null, after: after ?? null } };
}

/* ─── Support placeholders ───────────────────────────────────────── */

export interface AdminSupportActionQueueParams {
  actionType?: string;
  page?: number;
  pageSize?: number;
}

const SUPPORT_QUEUE_STATE_SET = new Set<string>(SUPPORT_ACTION_QUEUE_STATES);
const SUPPORT_SEVERITY_SET = new Set<string>(["info", "warn", "crit", "ok"]);

/**
 * Map a backend support-queue row onto the dashboard `AdminSupportActionQueueRow`.
 *
 * The backend row omits `queueId`, `source`, `state`, `requestedLabel`, and
 * `policy` (it returns userId/displayName/email/actionType/severity/reason/
 * detectedAt/membershipState/accountStatus). The Support page reads
 * `row.source.toLowerCase()` while filtering and renders `row.state`, so those
 * MUST be present strings — undefined would crash the page on filter / render.
 */
function normalizeSupportQueueRow(
  raw: Record<string, unknown>,
  index: number,
): AdminSupportActionQueueRow {
  const userId = String(raw.userId ?? raw.user_id ?? "");
  const actionType = typeof raw.actionType === "string" ? raw.actionType : "";
  const email = typeof raw.email === "string" ? raw.email : "";
  const severity = raw.severity;
  const state = raw.state;
  return {
    queueId:
      typeof raw.queueId === "string" && raw.queueId
        ? raw.queueId
        : `${userId}__${actionType}__${index}`,
    userId,
    displayName:
      typeof raw.displayName === "string" && raw.displayName
        ? raw.displayName
        : email,
    email,
    actionType,
    severity:
      typeof severity === "string" && SUPPORT_SEVERITY_SET.has(severity)
        ? (severity as AdminSupportActionSeverity)
        : "info",
    // Backend rows are system-derived (locked users, pending deletions, …);
    // default the missing source so the "automatic" source filter matches.
    source: typeof raw.source === "string" && raw.source ? raw.source : "automatic",
    state:
      typeof state === "string" && SUPPORT_QUEUE_STATE_SET.has(state)
        ? (state as SupportActionQueueState)
        : "pending",
    reason: typeof raw.reason === "string" ? raw.reason : "",
    policy: typeof raw.policy === "string" ? raw.policy : null,
    requestedLabel:
      typeof raw.requestedLabel === "string" ? raw.requestedLabel : "",
    detectedAt: typeof raw.detectedAt === "string" ? raw.detectedAt : null,
    membershipState:
      typeof raw.membershipState === "string" ? raw.membershipState : null,
    accountStatus:
      typeof raw.accountStatus === "string" ? raw.accountStatus : null,
  };
}

/**
 * GET /admin/support-actions/queue — backend returns derived queue rows.
 */
export async function listAdminSupportActionQueue(
  params: AdminSupportActionQueueParams = {},
): Promise<AdminPage<AdminSupportActionQueueRow>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.supportActionQueue, {
    params: pickQuery(params as Record<string, unknown>),
  });
  const page = toAdminPage<Record<string, unknown>>(data, params);
  return {
    ...page,
    items: page.items.map((raw, i) => normalizeSupportQueueRow(raw, i)),
  };
}


/**
 * IMPORTANT shape gap (documented in docs/admin-api-mode.md):
 *
 * The frontend `AdminSupportActionPlaceholder` is a *queue row* shape
 * (one row per `userId × actionType`). The backend's
 * /admin/support-actions/placeholders returns a *catalogue* shape
 * (action code + label + permission). The two don't translate without
 * a per-user fan-out the backend doesn't yet compute.
 *
 * For now in real mode we hit the endpoint to keep the surface live and
 * surface any 4xx/5xx, but return an empty array because the existing
 * frontend type can't be safely constructed from the catalogue. The
 * mock client continues to return the placeholder queue rows.
 *
 * TODO: align on either a queue endpoint (`/admin/support-actions/queue`)
 * or a frontend type that mirrors the catalogue shape.
 */
export async function listAdminSupportActionPlaceholders(): Promise<
  AdminSupportActionPlaceholder[]
> {
  const data = await get<unknown>(
    ADMIN_READ_ENDPOINTS.supportActionPlaceholders,
  );
  if (Array.isArray(data)) {
    return data.filter((row): row is AdminSupportActionPlaceholder => {
      return (
        typeof row === "object" &&
        row !== null &&
        "userId" in row &&
        "actionType" in row
      );
    });
  }
  if (
    typeof data === "object" &&
    data !== null &&
    Array.isArray((data as { items?: unknown }).items)
  ) {
    const items = (data as { items: unknown[] }).items;
    return items.filter((row): row is AdminSupportActionPlaceholder => {
      return (
        typeof row === "object" &&
        row !== null &&
        "userId" in row &&
        "actionType" in row
      );
    });
  }
  return [];
}

/* ─── Staging demo controls (L71/L72/L73I) ───────────────────────────
 *
 * READ: per-user dunning projection + global lifecycle-notice counts.
 * MUTATION: the two guarded staging-only demo actions. The backend is
 * additionally fail-closed (flag off / production / non-synthetic → refused),
 * so these are safe to expose behind the billing:simulate gate. Responses are
 * SAFE counts/flags only — no secret, customer id, or raw Stripe payload.
 */

export async function getAdminUserBillingRetry(
  userId: string,
): Promise<AdminBillingRetry> {
  return get<AdminBillingRetry>(
    ADMIN_READ_ENDPOINTS.userBillingRetry(userId),
  );
}

/**
 * W65-3 — the member's payments to Zone: the same list as their own Billing page,
 * read live from Stripe by the gateway. Shape-checked as a whole (see
 * `normalizePaymentHistory`): anything unexpected reads as "unavailable", never as
 * an empty list that would tell support the member paid nothing.
 */
export async function getAdminUserPayments(
  userId: string,
): Promise<AdminPaymentHistory> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.userPayments(userId));
  return normalizePaymentHistory(data);
}

export async function getAdminMembershipNoticeSummary(): Promise<AdminMembershipNoticeSummary> {
  const data = await get<unknown>(
    ADMIN_READ_ENDPOINTS.membershipNoticesSummary,
  );
  return normalizeNoticeSummary(data);
}

/* ─── Fulfillment / notice list / webhook-health read surfaces (L67B) ──
 *
 * Tolerant coercion: every reader accepts BOTH the real backend's camelCase
 * AND snake_case candidates, defaults missing scalars to null / 0, and derives
 * `healthy` client-side so a panel never crashes on an unexpected staging
 * payload. Safe fields only — no raw provider error string is ever surfaced.
 */

function pickStr(
  raw: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

function pickIdStr(
  raw: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v) return v;
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return null;
}

function pickNum(
  raw: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

/**
 * Build an `AdminPage<T>` from the new read surfaces, which return either
 * `{ items, total, page, pageSize }` (shipments) or `{ items, limit, offset }`
 * (notice / webhook lists). Maps each row through `map`.
 */
function toItemsPage<T>(
  data: unknown,
  map: (raw: Record<string, unknown>) => T,
  params: { page?: number; pageSize?: number } = {},
): AdminPage<T> {
  const obj =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const rawItems: unknown[] = Array.isArray(obj.items)
    ? obj.items
    : Array.isArray(data)
      ? (data as unknown[])
      : [];
  const items = rawItems.map((r) => map((r ?? {}) as Record<string, unknown>));
  return {
    items,
    total: typeof obj.total === "number" ? obj.total : items.length,
    page: typeof obj.page === "number" ? obj.page : (params.page ?? 1),
    pageSize:
      typeof obj.pageSize === "number"
        ? obj.pageSize
        : typeof obj.limit === "number"
          ? obj.limit
          : (params.pageSize ?? items.length),
  };
}

function normalizeShipment(raw: Record<string, unknown>): AdminShipmentListItem {
  return {
    membershipId: pickIdStr(raw, "membershipId", "membership_id"),
    fulfillmentOrderId: pickStr(
      raw,
      "fulfillmentOrderId",
      "fulfillment_order_id",
    ),
    shipmentId: pickStr(raw, "shipmentId", "shipment_id"),
    shipmentStatus: pickStr(raw, "shipmentStatus", "shipment_status", "status"),
    carrier: pickStr(raw, "carrier"),
    trackingNumber: pickStr(raw, "trackingNumber", "tracking_number"),
    trackingStatus: pickStr(raw, "trackingStatus", "tracking_status"),
    shippedAt: pickStr(raw, "shippedAt", "shipped_at"),
    deliveredAt: pickStr(raw, "deliveredAt", "delivered_at"),
    membershipState: pickStr(raw, "membershipState", "membership_state"),
    carrierClaims: normalizeCarrierClaims(raw.carrierClaims ?? raw.carrier_claims),
  };
}

/** W57-3: the order's filed claims, in the gateway's order (newest first). */
function normalizeCarrierClaims(raw: unknown): AdminShipmentCarrierClaim[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((claim) => {
    const normalized = normalizeCarrierClaim(claim);
    return normalized ? [normalized] : [];
  });
}

/** A filed claim is kept only when well formed — a claim with no reference is none. */
function normalizeCarrierClaim(raw: unknown): AdminShipmentCarrierClaim | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const claim = raw as Record<string, unknown>;
  const claimReference = pickStr(claim, "claimReference", "claim_reference")?.trim();
  if (!claimReference) return null;
  return {
    shipmentId: pickStr(claim, "shipmentId", "shipment_id"),
    claimReference,
    carrier: pickStr(claim, "carrier"),
    filedAt: pickStr(claim, "filedAt", "filed_at"),
  };
}

export async function listAdminShipments(
  params: AdminShipmentListParams = {},
): Promise<AdminPage<AdminShipmentListItem>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.listShipments, {
    params: pickQuery(params as Record<string, unknown>),
  });
  return toItemsPage<AdminShipmentListItem>(data, normalizeShipment, params);
}

function normalizeNoticeRow(
  raw: Record<string, unknown>,
): AdminMembershipNoticeRow {
  return {
    noticeId: pickIdStr(raw, "noticeId", "notice_id", "id") ?? "",
    membershipId: pickIdStr(raw, "membershipId", "membership_id"),
    userId: pickIdStr(raw, "userId", "user_id"),
    noticeType: pickStr(raw, "noticeType", "notice_type"),
    channel: pickStr(raw, "channel"),
    status: pickStr(raw, "status"),
    recipientMasked: pickStr(raw, "recipientMasked", "recipient_masked"),
    createdAt: pickStr(raw, "createdAt", "created_at"),
    sentAt: pickStr(raw, "sentAt", "sent_at"),
    failedReason: pickStr(
      raw,
      "failedReason",
      "failed_reason",
      "lastErrorCode",
      "last_error_code",
    ),
    retryScheduledAt: pickStr(
      raw,
      "retryScheduledAt",
      "retry_scheduled_at",
      "nextAttemptAt",
      "next_attempt_at",
    ),
  };
}

export async function listAdminMembershipNoticesFailed(
  params: AdminMembershipNoticeListParams = {},
): Promise<AdminPage<AdminMembershipNoticeRow>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.membershipNoticesFailed, {
    params: pickQuery(params as Record<string, unknown>),
  });
  return toItemsPage<AdminMembershipNoticeRow>(data, normalizeNoticeRow, params);
}

export async function listAdminMembershipNoticesDuePending(
  params: AdminMembershipNoticeListParams = {},
): Promise<AdminPage<AdminMembershipNoticeRow>> {
  const data = await get<unknown>(
    ADMIN_READ_ENDPOINTS.membershipNoticesDuePending,
    { params: pickQuery(params as Record<string, unknown>) },
  );
  return toItemsPage<AdminMembershipNoticeRow>(data, normalizeNoticeRow, params);
}

export async function getAdminOutboxHealth(): Promise<AdminOutboxHealth> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.outboxHealth);
  const r =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const counts =
    r.counts && typeof r.counts === "object"
      ? (r.counts as Record<string, unknown>)
      : {};
  const failed =
    pickNum(r, "failed") ??
    pickNum(counts, "failed") ??
    pickNum(r, "dueFailedCount") ??
    0;
  const deadLettered =
    pickNum(r, "deadLettered", "dead_lettered") ??
    pickNum(counts, "deadLettered", "dead_lettered") ??
    pickNum(r, "deadCount") ??
    0;
  return {
    pending:
      pickNum(r, "pending") ??
      pickNum(counts, "pending") ??
      pickNum(r, "duePendingCount") ??
      0,
    failed,
    deadLettered,
    oldestPendingAgeSeconds: pickNum(
      r,
      "oldestPendingAgeSeconds",
      "oldest_pending_age_seconds",
    ),
    publishedLast24h: pickNum(r, "publishedLast24h", "published_last_24h"),
    healthy: failed === 0 && deadLettered === 0,
  };
}

export async function getAdminEasypostWebhookHealth(): Promise<AdminEasypostWebhookHealth> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.easypostWebhookHealth);
  const r =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const counts =
    r.counts && typeof r.counts === "object"
      ? (r.counts as Record<string, unknown>)
      : {};
  const enabled = r.enabled === true;
  const failed =
    pickNum(r, "failed", "failedCount") ?? pickNum(counts, "failed") ?? 0;
  const stale =
    pickNum(r, "stale", "staleReceivedCount") ?? pickNum(counts, "stale") ?? 0;
  return {
    enabled,
    received:
      pickNum(r, "received", "receivedCount") ?? pickNum(counts, "received") ?? 0,
    processed:
      pickNum(r, "processed", "processedCount", "enqueuedCount") ??
      pickNum(counts, "processed") ??
      0,
    failed,
    stale,
    healthy: enabled && failed === 0 && stale === 0,
  };
}

function normalizeWebhookEvent(
  raw: Record<string, unknown>,
): AdminWebhookEventRow {
  return {
    eventId: pickIdStr(raw, "eventId", "event_id", "id") ?? "",
    source: pickStr(raw, "source", "provider"),
    eventType: pickStr(raw, "eventType", "event_type"),
    status: pickStr(
      raw,
      "status",
      "processingStatus",
      "processing_status",
      "normalizedStatus",
      "normalized_status",
    ),
    receivedAt: pickStr(raw, "receivedAt", "received_at"),
    processedAt: pickStr(raw, "processedAt", "processed_at"),
    acknowledgedAt: pickStr(raw, "acknowledgedAt", "acknowledged_at"),
    error: pickStr(
      raw,
      "error",
      "errorCode",
      "error_code",
      "errorCategory",
      "error_category",
    ),
  };
}

export async function listAdminEasypostWebhookEvents(
  params: AdminWebhookEventListParams = {},
): Promise<AdminPage<AdminWebhookEventRow>> {
  const data = await get<unknown>(ADMIN_READ_ENDPOINTS.easypostWebhookEvents, {
    params: pickQuery(params as Record<string, unknown>),
  });
  return toItemsPage<AdminWebhookEventRow>(data, normalizeWebhookEvent, params);
}

export async function simulateFailedRenewal(
  body: SimulateFailedRenewalRequest,
): Promise<SimulateFailedRenewalResponse> {
  return post<SimulateFailedRenewalResponse>(
    ADMIN_MUTATION_ENDPOINTS.simulateFailedRenewal,
    body,
  );
}

export async function cleanupSyntheticUser(
  body: CleanupSyntheticUserRequest,
): Promise<CleanupSyntheticUserResponse> {
  return post<CleanupSyntheticUserResponse>(
    ADMIN_MUTATION_ENDPOINTS.cleanupSyntheticUser,
    body,
  );
}

/**
 * Load unassigned hardware serials into the reservable fulfillment pool.
 * Real-only (a guarded `/admin/*` route — there is no mock variant). The
 * backend is partial-success + idempotent, so the response carries
 * created/updated/rejected counts.
 */
export async function loadSerialInventory(
  body: AdminSerialLoadRequest,
): Promise<AdminSerialLoadResponse> {
  return post<AdminSerialLoadResponse>(
    ADMIN_MUTATION_ENDPOINTS.loadSerialInventory,
    body,
  );
}

/* ─── Test seam ──────────────────────────────────────────────────── */

/**
 * Exposed for tests so they can inspect the pagination/query-mapping
 * helpers without re-implementing them.
 */
export const __internal__ = { toAdminPage, pickQuery, authHeaders };
